import React, { useContext } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import * as Yup from 'yup';
import * as qs from 'query-string';
import gql from 'graphql-tag';
import { useHistory, useLocation } from 'react-router-dom';
import { LegacyInputTextComponent } from '../components/prime-formik';
import { growlErrorsWithMessage } from '../utils/NotificationUtil';
import { NotificationContext } from '../components/GrowlNotifications';
import runAcessTokenChecker from '../services/auth/auth-scheduler';
import apollo from '../services/apollo';
import { persistCredentialsSession } from '../services/auth/auth-storage';
import LockedOut from '../components/LockedOut';
import useLockoutMechanism from '../hooks/useLockoutMechanism';

const CONFIRM_MULTI_FACTOR_AUTHENTICATION = gql`
  mutation($input: ConfirmMultiFactorAuthenticationInput!) {
    confirmMultiFactorAuthentication(input: $input) {
      token
      refreshToken
      error {
        type
        lockedOutTimestamp
      }
    }
  }
`;

const RESEND_CONFIRMATION_CODE_EMAIL = gql`
  mutation($userId: ID!) {
    resendConfirmationCodeEmail(userId: $userId)
  }
`;

const i18n = defineMessages({
  codeIsRequired: {
    id: 'login.multi_factor_authentication.confirmation_code_required',
    defaultMessage: 'Confirmation code is required',
  },
  invalidCode: { id: 'login.multi_factor_authentication.invalid_code', defaultMessage: 'Invalid code' },
});

const MultiFactorAuthenticationConfirmation = () => {
  const location = useLocation();
  const history = useHistory();
  const growl = useContext(NotificationContext);

  const { userId } = qs.parse(location.search);
  const { formatMessage } = useIntl();

  const { lockedOut, timeUntilLockoutExpiration, lockOut } = useLockoutMechanism();

  const sendMultiFactorAuthenticationConfirmationCode = (code) =>
    apollo
      .mutate({
        mutation: CONFIRM_MULTI_FACTOR_AUTHENTICATION,
        variables: {
          input: {
            userId,
            code,
          },
        },
      })
      .then((res) => res.data);

  const resendConfirmationCodeEmail = () =>
    apollo
      .mutate({
        mutation: RESEND_CONFIRMATION_CODE_EMAIL,
        variables: {
          userId,
        },
      })
      .then((res) => res.data);

  const confirmMultiFactorAuthentication = async (values, resetForm) => {
    const { code } = values;

    try {
      const result = await sendMultiFactorAuthenticationConfirmationCode(code);

      if (
        result &&
        result.confirmMultiFactorAuthentication.token &&
        result.confirmMultiFactorAuthentication.refreshToken
      ) {
        const { token, refreshToken } = result.confirmMultiFactorAuthentication;

        persistCredentialsSession(token, refreshToken);

        runAcessTokenChecker();

        history.push('/');
      }

      if (result && result.confirmMultiFactorAuthentication.error) {
        const { error } = result.confirmMultiFactorAuthentication;

        const { type, lockedOutTimestamp } = error;

        resetForm();

        if (type === 'INVALID_CREDENTIALS') {
          growlErrorsWithMessage(growl, formatMessage(i18n.invalidCode));
        }

        if (type === 'LOCKED_OUT') {
          lockOut(lockedOutTimestamp);

          growlErrorsWithMessage(growl, formatMessage(i18n.lockedOut));
        }
      }
    } catch (error) {
      resetForm();
    }
  };

  if (lockedOut && timeUntilLockoutExpiration) {
    return <LockedOut timeUntilLockoutExpiration={timeUntilLockoutExpiration} />;
  }

  const initialValues = {
    code: '',
  };

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(formatMessage(i18n.codeIsRequired)),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikBag) => {
        const { resetForm } = formikBag;
        confirmMultiFactorAuthentication(values, resetForm);
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <div className="p-grid p-fluid p-justify-center logo-top">
            <img src="images/default-195.png" className="brand center-block" alt="GPI Logo" />
          </div>
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-12 p-md-3">
              <FormattedMessage
                id="login.multi_factor_authentication.instruction"
                defaultMessage="You have been sent a confirmation code by email. Enter the code you received."
              >
                {(text) => <h4 className="center-aligned-instructions">{text}</h4>}
              </FormattedMessage>
            </div>
          </div>
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-12 p-md-3">
              <FormattedMessage id="login.multi_factor_authentication.code" defaultMessage="Code">
                {(text) => <Field id="code" name="code" component={LegacyInputTextComponent} placeholder={text} />}
              </FormattedMessage>
            </div>
          </div>
          <br />
          <div className="p-grid p-fluid p-justify-center">
            <div className="p-col-4 p-md-1">
              <FormattedMessage id="login.multi_factor_authentication.confirmation" defaultMessage="Confirm">
                {(text) => <Button type="submit" label={text} disabled={isSubmitting} />}
              </FormattedMessage>
            </div>
            <div className="p-col-4 p-md-1">
              <FormattedMessage id="login.multi_factor_authentication.resend_email" defaultMessage="Resend email">
                {(text) => (
                  <Button
                    type="button"
                    label={text}
                    className="p-button-secondary"
                    onClick={() => resendConfirmationCodeEmail()}
                    disabled={isSubmitting}
                  />
                )}
              </FormattedMessage>
            </div>
            <div className="p-col-4 p-md-1">
              <FormattedMessage id="login.multi_factor_authentication.back_to_login" defaultMessage="Back to login">
                {(text) => (
                  <Button
                    type="button"
                    label={text}
                    className="p-button-secondary"
                    onClick={() => history.push('/')}
                    disabled={isSubmitting}
                  />
                )}
              </FormattedMessage>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default MultiFactorAuthenticationConfirmation;
